import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  CalendarDays,
  Clock3,
  Grid3X3,
  Heart,
  Leaf,
  MapPin,
  Search,
  ShieldCheck,
  Star,
  TicketCheck,
  Users,
} from "lucide-react";
import "./Cities_activities.css";
import {
  formatPrice as formatActivityPrice,
  getCitiesActivitiesPage,
  getVisibleActivities,
} from "./Cities_activities";
import usePageMeta from "../../../../hooks/usePageMeta";

const ADMIN_ACTIVITIES_KEY = "mbt_admin_activities";

function readAdminActivities() {
  try {
    return JSON.parse(localStorage.getItem(ADMIN_ACTIVITIES_KEY) || "[]");
  } catch {
    return [];
  }
}

function mapAdminActivity(activity, fallbackImage) {
  return {
    id: activity.id,
    title: activity.title,
    location: activity.location || activity.city,
    rating: Number(activity.rating || 0),
    reviews: Number(activity.reviews || 0),
    duration: activity.duration || "Flexible",
    image: activity.imageUrl || fallbackImage,
    price: Number(activity.price || 0),
    oldPrice: Number(activity.oldPrice || activity.price || 0),
    desc: activity.description || "Explore this handpicked activity with easy booking and trusted support.",
  };
}

/*
const filterGroups = [
  { title: "Price", type: "range", lines: ["₹ 100 - ₹ 12,312"] },
  { title: "Duration", lines: ["Up to 1 hour", "1 to 4 hours", "4 hours to 1 day", "1 to 3 days", "More than 3 days"] },
  { title: "User Rating", lines: ["5 stars", "4 stars & up", "3 stars & up", "2 stars & up"] },
  { title: "Specials", lines: ["Free Cancellation", "Skip The Line", "Private Tour", "Special Offer"] },
];

function formatPrice(value) {
  return `₹ ${Number(value).toLocaleString("en-IN")}`;
}

*/

export default function CitiesActivities() {
  usePageMeta(
  "City Activities & Things to Do in India – MakeBeeTrip",
  "Browse handpicked activities and experiences in India's top cities — sightseeing, adventure, culture and day tours. Book city activities on MakeBeeTrip.",
  "city activities india, things to do in india, india sightseeing, day tours india, adventure activities india, city experiences"
);
  const location = useLocation();
  const navigate = useNavigate();
  const pageData = useMemo(() => getCitiesActivitiesPage(new URLSearchParams(location.search)), [location.search]);
  const { activeCity, categoryChips, cityOptions, filterGroups, sortOptions } = pageData;
  const [search, setSearch] = useState("");
  const [where, setWhere] = useState(activeCity.label);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [sort, setSort] = useState("");
  const [filterResetKey, setFilterResetKey] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [adminActivities, setAdminActivities] = useState(readAdminActivities);

  useEffect(() => {
    AOS.init({ duration: 650, once: true, easing: "ease-out-cubic", offset: 50 });
    const timer = setTimeout(() => AOS.refresh(), 80);
    return () => clearTimeout(timer);
  }, []);

  const mergedActivities = useMemo(() => {
    const fallbackImage = pageData.activities[0]?.image;
    const savedForCity = adminActivities
      .filter((activity) => activity.cityCode === activeCity.code || activity.city === activeCity.short)
      .map((activity) => mapAdminActivity(activity, fallbackImage));

    return [...savedForCity, ...pageData.activities];
  }, [activeCity.code, activeCity.short, adminActivities, pageData.activities]);
  const activities = useMemo(
    () => getVisibleActivities(mergedActivities, { search, sort }),
    [mergedActivities, search, sort]
  );

  useEffect(() => {
    setWhere(activeCity.label);
  }, [activeCity.label]);

  useEffect(() => {
    const syncAdminActivities = () => setAdminActivities(readAdminActivities());
    window.addEventListener("storage", syncAdminActivities);
    window.addEventListener("focus", syncAdminActivities);
    return () => {
      window.removeEventListener("storage", syncAdminActivities);
      window.removeEventListener("focus", syncAdminActivities);
    };
  }, []);

  const handleReset = () => {
    setSearch("");
    setWhere(activeCity.label);
    setDate(new Date().toISOString().slice(0, 10));
    setSort("");
    setFilterResetKey((key) => key + 1);
  };

  const handleChipSelect = (chip) => {
    setSearch("");
    setSort("");
    setFilterResetKey((key) => key + 1);
    navigate(`/cities-activities?toCode=${chip.code}&city=${encodeURIComponent(chip.title)}`);
  };

  return (
    <main className="ca-results-page">
      <section className="cities-activities-hero">
        <div className="ca-hero-content">
          <div className="ca-title-block">
            <div>
              <h1>Activities <Star size={18} /></h1>
              <p>Handpicked experiences & activities for your journey</p>
            </div>
          </div>

          <div className="ca-perks">
            {[
              ["Curated Experiences", "Handpicked just for you", ShieldCheck],
              ["Best Prices", "Guaranteed", TicketCheck],
              ["Easy Booking", "Hassle-free", CalendarDays],
              ["24/7 Support", "We're here for you", Users],
            ].map(([title, subtitle, Icon]) => (
              <div className="ca-perk" key={title}>
                <Icon size={14} />
                <span>
                  <strong>{title}</strong>
                  <small>{subtitle}</small>
                </span>
              </div>
            ))}
          </div>

        <form className="ca-top-search" autoComplete="off" onSubmit={(event) => event.preventDefault()}>
          <label className="ca-top-field">
            <span className="ca-top-icon"><MapPin size={17} /></span>
            <span>
              <small>Where?</small>
              <input
                value={where}
                onChange={(event) => setWhere(event.target.value)}
                list="ca-city-options"
                autoComplete="off"
                placeholder="Search for a location or activity"
              />
            </span>
            <datalist id="ca-city-options">
              {cityOptions.map((city) => (
                <option key={city.code} value={city.label} />
              ))}
            </datalist>
          </label>
          <label className="ca-top-field ca-top-field--date">
            <span className="ca-top-icon"><CalendarDays size={17} /></span>
            <span>
              <small>When?</small>
              <input type="date" value={date} onChange={(event) => setDate(event.target.value)} autoComplete="off" />
            </span>
          </label>
          <button type="submit" className="ca-top-search-btn">
            <Search size={18} />
            Search
          </button>
        </form>

          <div className="ca-categories" aria-label="Activity categories">
            <span className="ca-categories-label"><Grid3X3 size={14} /> Categories</span>
            {[
              ["All Activities", Grid3X3],
              ["Sightseeing", MapPin],
              ["Adventure", Star],
              ["Food & Experiences", Leaf],
              ["Culture & Heritage", ShieldCheck],
              ["Transfers", TicketCheck],
            //   ["Wellness", ShieldCheck],
            ].map(([label, Icon], index) => (
              <button key={label} type="button" className={index === 0 ? "is-active" : ""}>
                <Icon size={13} />
                {label}
              </button>
            ))}
          </div>

          <div className="ca-benefits">
            {[
              ["Best Prices", "Guaranteed", ShieldCheck],
              ["Easy Booking", "Hassle-free", TicketCheck],
              ["24/7 Support", "We're here", Users],
              ["Secure Payments", "100% Safe", ShieldCheck],
            ].map(([title, subtitle, Icon]) => (
              <div className="ca-benefit" key={title}>
                <Icon size={16} />
                <span>
                  <strong>{title}</strong>
                  <small>{subtitle}</small>
                </span>
              </div>
            ))}
          </div>

          <aside className="ca-quote-card mbt-animated-card" data-aos="fade-left" data-aos-delay="120">
            <strong>Discover unforgettable</strong>
            <span>experiences at every destination</span>
          </aside>
        </div>
      </section>

      <div className="ca-results-shell">
        <div className="ca-breadcrumb">Home &gt; Activities &gt; <span>{activeCity.short} Activities</span></div>

        <div className="ca-heading-row">
          <h1>{activeCity.short} <span>Activities</span></h1>
          <button type="button" className="ca-next-btn">›</button>
        </div>

        <div className="ca-filter-title-row">
          <strong>Filter</strong>
          <button
            type="button"
            className="ca-filter-toggle"
            onClick={() => setFiltersOpen((open) => !open)}
          >
            {filtersOpen ? "Hide Filters" : "Show Filters"}
          </button>
          <button type="button" onClick={handleReset}>Reset</button>
        </div>

        <div className="ca-chip-row">
          {categoryChips.map((chip) => (
            <div
              className={`ca-type-chip${chip.code === activeCity.code ? " is-active" : ""}`}
              key={chip.title}
              onClick={() => handleChipSelect(chip)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleChipSelect(chip);
                }
              }}
              role="link"
              tabIndex={0}
            >
              <img src={chip.image} alt="" />
              <span>{chip.title}</span>
            </div>
          ))}
        </div>

        <div className="ca-results-layout">
          <aside className={`ca-filter-panel mbt-animated-card${filtersOpen ? " is-open" : ""}`} key={filterResetKey} data-aos="fade-right">
            <div className="ca-sidebar-tools">
              <label className="ca-list-search">
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search activities..."
                  autoComplete="off"
                />
                <Search size={15} />
              </label>

              <div className="ca-sort-controls">
                <span>Sort By</span>
                {sortOptions.map((item) => (
                  <label className="ca-sort-check" key={item}>
                    <input
                      type="checkbox"
                      checked={sort === item}
                      onChange={(event) => setSort(event.target.checked ? item : "")}
                    />
                    <span>{item}</span>
                  </label>
                ))}
              </div>
            </div>

            {filterGroups.map((group) => (
              <div className="ca-filter-group" key={group.title}>
                <div className="ca-filter-title">
                  <span>{group.title}</span>
                </div>
                {group.type === "range" ? (
                  <div className="ca-price-filter">
                    <div className="ca-range-line"><span /></div>
                    <small>{group.lines[0]}</small>
                  </div>
                ) : (
                  <div className="ca-check-list">
                    {group.lines.map((line) => (
                      <label key={line}>
                        <input type="checkbox" />
                        <span>{line}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <button type="button" className="ca-show-more">Show More +</button>
          </aside>

          <section className="ca-card-list">
            {activities.map((item) => (
              <article
                className="ca-activity-card mbt-animated-card"
                key={item.id || item.title}
                data-aos="fade-up"
              >
                <div className="ca-card-image">
                  <img src={item.image} alt={item.title} />
                  <button type="button" aria-label="Save activity"><Heart size={15} /></button>
                  <div className="ca-image-rating">
                    <Star size={15} />
                    <strong>{item.rating}</strong>
                    <span>({item.reviews} reviews)</span>
                  </div>
                </div>
                <div className="ca-card-body">
                  <h2>{item.title}</h2>
                  <div className="ca-meta-line">
                    <span><MapPin size={13} /> {item.location}</span>
                    <span><Clock3 size={13} /> {item.duration}</span>
                    <span className="ca-rating"><Star size={13} /> {item.rating} ({item.reviews} reviews)</span>
                  </div>
                  <p>{item.desc}</p>
                  <div className="ca-feature-row">
                    {["Audio Visual Experience", "Live Narration", "Stunning Light Projections", "Trusted & Safe"].map((feature) => (
                      <span key={feature}>
                        <ShieldCheck size={15} />
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="ca-card-price">
                  <strong>{formatActivityPrice(item.price)}</strong>
                  <small>Per Person</small>
                  <del>{formatActivityPrice(item.oldPrice)}</del>
                  <span><ShieldCheck size={13} /> Free Cancellation</span>
                  <button type="button" onClick={() => navigate("/activities/confirm", { state: { activity: item } })}>Book Now</button>
                </div>
              </article>
            ))}

            <div className="ca-ad-card mbt-animated-card" data-aos="fade-up" data-aos-delay="160">
              <div>
                <strong>AMERICO Forts of Epic</strong>
                <span>A Land of Endless Wonders.</span>
              </div>
              <button type="button">Know More</button>
            </div>
          </section>
        </div>
      </div>
    </main>
  );
}
